import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publishingAPI } from '../services/api';
import { useToast } from './Toast';
import '../styles/WorkspaceChrome.css';
import './InProgress.css';
import './AccountPage.css';

function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState(null);
  const [connLoading, setConnLoading] = useState(true);
  const [siteUrl, setSiteUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [label, setLabel] = useState('');
  const [savingConn, setSavingConn] = useState(false);
  const [testingConn, setTestingConn] = useState(false);

  const loadConnection = useCallback(async () => {
    try {
      setConnLoading(true);
      const res = await publishingAPI.getWordPressConnection();
      const c = res.data.connection;
      setConnection(c);
      if (c) {
        setSiteUrl(c.site_url || '');
        setLabel(c.label || '');
      }
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Failed to load WordPress connection',
        'error'
      );
    } finally {
      setConnLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void loadConnection();
  }, [loadConnection]);

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Logout failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveConnection = async () => {
    if (!siteUrl.trim()) {
      showToast('Site URL is required.', 'error');
      return;
    }
    if (!connection?.api_key_set && !apiKey.trim()) {
      showToast('API key is required.', 'error');
      return;
    }
    try {
      setSavingConn(true);
      const body = { site_url: siteUrl.trim(), label: label.trim() || undefined };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const res = await publishingAPI.saveWordPressConnection(body);
      setConnection(res.data.connection);
      setApiKey('');
      showToast('WordPress connection saved.', 'success');
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Failed to save connection',
        'error'
      );
    } finally {
      setSavingConn(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConn(true);
      const res = await publishingAPI.testWordPressConnection();
      if (res.data.connection) setConnection(res.data.connection);
      showToast(res.data.message || (res.data.ok ? 'Connection OK' : 'Test failed'), res.data.ok ? 'success' : 'error');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      showToast(msg || 'Connection test failed', 'error');
      if (err.response?.data?.connection) setConnection(err.response.data.connection);
      void loadConnection();
    } finally {
      setTestingConn(false);
    }
  };

  const handleRemoveConnection = async () => {
    if (!window.confirm('Remove WordPress connection? You can reconnect later.')) return;
    try {
      await publishingAPI.deleteWordPressConnection();
      setConnection(null);
      setSiteUrl('');
      setApiKey('');
      setLabel('');
      showToast('WordPress connection removed.', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Remove failed', 'error');
    }
  };

  const testStatusLine = () => {
    if (!connection?.last_tested_at) return null;
    const when = new Date(connection.last_tested_at).toLocaleString();
    return connection.last_test_ok
      ? `Last tested ${when} — OK`
      : `Last tested ${when} — failed`;
  };

  return (
    <div className="in-progress pf-explore account-page">
      <div className="pf-header">
        <div>
          <h2>Account & publishing</h2>
          <p className="pf-sub">Signed in as {user?.email || '—'}</p>
        </div>
      </div>

      <section className="pf-list-card account-wp-card">
        <h3>WordPress connection</h3>
        <p className="pf-empty-hint account-wp-intro">
          Connect your WordPress site running the Racontera plugin. Generate an API key under{' '}
          <strong>Settings → Racontera</strong> in wp-admin. The key is stored encrypted and never
          shown again after save. For local Valet sites use{' '}
          <strong>http://yoursite.test</strong> (not https).
        </p>

        {connLoading ? (
          <p className="muted">Loading connection…</p>
        ) : (
          <>
            <div className="account-wp-field">
              <label htmlFor="wp-site-url">Site URL</label>
              <input
                id="wp-site-url"
                type="url"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="http://mplwp.test"
                className="account-wp-input"
              />
            </div>
            <div className="account-wp-field">
              <label htmlFor="wp-label">Label (optional)</label>
              <input
                id="wp-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Local dev site"
                className="account-wp-input"
              />
            </div>
            <div className="account-wp-field">
              <label htmlFor="wp-api-key">
                API key {connection?.api_key_set ? '(leave blank to keep current)' : ''}
              </label>
              <input
                id="wp-api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="rac_…"
                className="account-wp-input"
                autoComplete="off"
              />
            </div>
            {testStatusLine() && (
              <p className={`account-wp-test-status ${connection?.last_test_ok ? 'ok' : 'fail'}`}>
                {testStatusLine()}
              </p>
            )}
            <div className="account-wp-actions">
              <button
                type="button"
                className="pf-btn pf-btn-primary"
                disabled={savingConn}
                onClick={() => void handleSaveConnection()}
              >
                {savingConn ? 'Saving…' : 'Save connection'}
              </button>
              <button
                type="button"
                className="pf-btn pf-btn-secondary"
                disabled={testingConn || !connection?.api_key_set}
                onClick={() => void handleTestConnection()}
              >
                {testingConn ? 'Testing…' : 'Test connection'}
              </button>
              {connection && (
                <button
                  type="button"
                  className="pf-btn pf-btn-inline-danger"
                  onClick={() => void handleRemoveConnection()}
                >
                  Remove
                </button>
              )}
            </div>
          </>
        )}
      </section>

      <div className="pf-list-card account-placeholder-card">
        <p className="account-placeholder-lead">
          Other workspace preferences and governance settings will live here.
        </p>
        <button
          type="button"
          className="btn-secondary-sm account-logout-btn"
          onClick={() => void handleLogout()}
          disabled={busy}
        >
          {busy ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </div>
  );
}

export default AccountPage;
